import React from 'react';
import { format, parseISO } from 'date-fns';
import { Hourglass, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { AutoStatusLastRun, FinishingSoonDoctorGroup, FinishingSoonRow } from '@/services/finishingSoonService';

export const formatCloseDate = (iso: string): string => {
  try {
    return format(parseISO(iso), 'd MMM yyyy');
  } catch {
    return iso;
  }
};

interface FinishingSoonBarProps {
  count: number;
  doctors: FinishingSoonDoctorGroup[];
  active: boolean;
  onToggle: () => void;
  doctorKey: string | null;
  onDoctorChange: (key: string | null) => void;
  lastRun: AutoStatusLastRun | null;
  isLoading?: boolean;
}

/**
 * The "Finishing soon (n)" chip, its per-doctor counts, and a one-line proof the daily job is running.
 * Names appear only inside the app.
 */
export const FinishingSoonBar: React.FC<FinishingSoonBarProps> = ({
  count,
  doctors,
  active,
  onToggle,
  doctorKey,
  onDoctorChange,
  lastRun,
  isLoading = false,
}) => (
  <div className="flex flex-col gap-2" data-testid="finishing-soon-bar">
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={active}
        data-testid="finishing-soon-chip"
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition',
          active
            ? 'border-amber-500 bg-amber-100 text-amber-950'
            : 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100',
        )}
      >
        <Hourglass className="h-3.5 w-3.5" />
        Finishing soon ({isLoading ? '…' : count})
      </button>
      <span className="text-xs text-slate-500" data-testid="finishing-soon-lastrun">
        {lastRun
          ? `Automatic close last ran ${format(parseISO(lastRun.run_at), 'd MMM, HH:mm')}: ${lastRun.closed} closed, ${lastRun.flagged} newly finishing soon${lastRun.errors ? `, ${lastRun.errors} errors` : ''}.`
          : 'Automatic close has not run yet.'}
      </span>
    </div>

    {active && (
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="finishing-soon-panel">
        <p className="text-sm text-amber-950">
          These cases have had no Plato visit for a long time and no appointment is booked. Each closes automatically as
          Referred Back on the date shown, with no email, unless a new visit appears or you press <strong>Keep open</strong>.
          You can also undo an automatic close in the status control.
        </p>
        {doctors.length === 0 ? (
          <p className="mt-2 text-sm text-slate-600">Nothing is finishing soon.</p>
        ) : (
          <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="finishing-soon-doctors">
            <span className="text-xs uppercase tracking-[0.14em] text-slate-500">By referring doctor</span>
            {doctors.map((doctor) => (
              <button
                key={doctor.key}
                type="button"
                onClick={() => onDoctorChange(doctorKey === doctor.key ? null : doctor.key)}
                aria-pressed={doctorKey === doctor.key}
                data-testid="finishing-soon-doctor"
                title={`Earliest closes on ${formatCloseDate(doctor.earliestClosesOn)}`}
                className={cn(
                  'rounded-full border px-2.5 py-0.5 text-xs transition',
                  doctorKey === doctor.key
                    ? 'border-slate-800 bg-slate-800 text-white'
                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100',
                )}
              >
                {doctor.label} ({doctor.count})
              </button>
            ))}
            {doctorKey && (
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => onDoctorChange(null)}>
                <Undo2 className="mr-1 h-3 w-3" />
                All doctors
              </Button>
            )}
          </div>
        )}
      </div>
    )}
  </div>
);

interface FinishingSoonRowNoteProps {
  row: FinishingSoonRow;
  onKeepOpen: () => void;
  disabled?: boolean;
}

/** Under a case in the list: the date it closes on, and the Keep open button. */
export const FinishingSoonRowNote: React.FC<FinishingSoonRowNoteProps> = ({ row, onKeepOpen, disabled }) => (
  <div className="mt-1.5 flex flex-wrap items-center gap-2" data-testid="finishing-soon-row-note">
    <span className="text-[11px] font-medium text-amber-900">
      Closes automatically on {formatCloseDate(row.closes_on)}
    </span>
    <span className="text-[11px] text-slate-500">last visit {formatCloseDate(row.last_visit_date)}</span>
    <Button
      variant="outline"
      size="sm"
      className="h-6 px-2 text-[11px]"
      onClick={onKeepOpen}
      disabled={disabled}
      data-testid="keep-open-button"
    >
      Keep open
    </Button>
  </div>
);
